/**
 * ColumnToggler — manages column visibility state and renders a Bootstrap 5
 * dropdown UI with checkboxes for toggling individual columns.
 *
 * Visibility changes trigger the onChange callback so the controller can
 * re-run the render pipeline.
 */
export class ColumnToggler {
  /**
   * @param {Array<Object>} columns - Column definitions with at least `field`, `title`, and optional `visible`
   * @param {Function} onChange - Callback invoked after every visibility change
   */
  constructor(columns, onChange) {
    this._columns = (columns || []).map((col) => ({
      ...col,
      visible: col.visible !== false,
      _initiallyHidden: col.visible === false,
    }))
    this._onChange = typeof onChange === 'function' ? onChange : () => {}
  }

  /**
   * Toggle the visibility of a column by field name.
   *
   * @param {string} fieldName
   */
  toggle(fieldName) {
    const col = this._columns.find((c) => c.field === fieldName)
    if (!col) return

    col.visible = !col.visible
    this._onChange()
  }

  /**
   * Show a column by field name (no-op if already visible).
   *
   * @param {string} fieldName
   */
  show(fieldName) {
    const col = this._columns.find((c) => c.field === fieldName)
    if (!col || col.visible) return

    col.visible = true
    this._onChange()
  }

  /**
   * Hide a column by field name (no-op if already hidden).
   *
   * @param {string} fieldName
   */
  hide(fieldName) {
    const col = this._columns.find((c) => c.field === fieldName)
    if (!col || !col.visible) return

    col.visible = false
    this._onChange()
  }

  /**
   * Return all columns that are currently visible.
   *
   * @returns {Array<Object>}
   */
  getVisibleColumns() {
    return this._columns.filter((col) => col.visible)
  }

  /**
   * Return the field names of all currently hidden columns.
   *
   * @returns {Array<string>}
   */
  getHiddenFields() {
    return this._columns
      .filter((col) => !col.visible)
      .map((col) => col.field)
  }

  /**
   * Render a Bootstrap 5 dropdown DOM element with a checkbox for each column.
   *
   * The dropdown button uses `btn btn-secondary dropdown-toggle` classes.
   * Each item is a `dropdown-item` containing a `form-check` with a
   * `form-check-input` checkbox and `form-check-label`.
   *
   * Clicking a checkbox calls `toggle(fieldName)` and updates the checked state.
   *
   * @returns {HTMLElement} A `<div class="dropdown">` element
   */
  renderDropdown() {
    const wrapper = document.createElement('div')
    wrapper.className = 'dropdown'

    const button = document.createElement('button')
    button.className = 'btn btn-secondary dropdown-toggle'
    button.setAttribute('type', 'button')
    button.setAttribute('data-bs-toggle', 'dropdown')
    button.setAttribute('aria-expanded', 'false')
    button.textContent = 'Columns'
    wrapper.appendChild(button)

    const menu = document.createElement('ul')
    menu.className = 'dropdown-menu'

    this._columns.forEach((col) => {
      // Skip columns that should not appear in the toggler:
      // - Hidden columns (not currently visible and were hidden from the start)
      // - Columns explicitly marked toggleable: false
      if (col.toggleable === false || !col.visible) return

      const li = document.createElement('li')
      li.className = 'dropdown-item'

      const formCheck = document.createElement('div')
      formCheck.className = 'form-check mb-0'

      const checkbox = document.createElement('input')
      checkbox.type = 'checkbox'
      checkbox.className = 'form-check-input'
      checkbox.id = `col-toggle-${col.field}`
      checkbox.checked = col.visible
      checkbox.addEventListener('change', () => {
        this.toggle(col.field)
        checkbox.checked = col.visible
      })

      const label = document.createElement('label')
      label.className = 'form-check-label'
      label.setAttribute('for', `col-toggle-${col.field}`)
      label.textContent = col.title || col.field

      formCheck.appendChild(checkbox)
      formCheck.appendChild(label)
      li.appendChild(formCheck)
      menu.appendChild(li)
    })

    wrapper.appendChild(menu)
    return wrapper
  }
}
